/**
 * Image source helper.
 *
 * Jednotné místo, kde rozhodneme jak pro konkrétní URL vygenerovat:
 *  - thumb URL (malá, rychlá, ořezaná na stejný poměr)
 *  - display URL (střední, s EXIF auto-rotací, pro hlavní náhled)
 *  - full URL (velká, pro zoom / lightbox)
 *  - srcset s více šířkami, aby si prohlížeč vybral tu pravou (úspora dat)
 *
 * Pokud je zdroj Cloudinary, použijeme jeho transformace. Jinak předáme
 * URL skrz server proxy `/api/image-proxy`, který (pokud je nasazený) vrátí
 * optimalizovanou/rotovanou verzi přes `sharp`. Když proxy není dostupná,
 * vrátí se originální URL a prohlížeč si poradí sám (EXIF orientaci
 * dnes všechny moderní prohlížeče respektují defaultně).
 *
 * Tahle jednotná fasáda je v souladu s architekturou projektu
 * (pevné jádro + pluginy) - konkrétní účty mohou mít jiný zdroj fotek.
 */

type Variant = 'thumb' | 'display' | 'full'

type VariantOptions = {
	width: number
	height?: number
	quality?: number
}

const VARIANTS: Record<Variant, VariantOptions> = {
	thumb: { width: 160, height: 120, quality: 60 },
	display: { width: 1280, quality: 78 },
	full: { width: 2400, quality: 88 },
}

/**
 * Šířky pro srcset. Prohlížeč si z nich vybere podle `sizes` a DPR.
 * Nechceme moc variant (každá je nová cache položka na CDN), ale zároveň
 * potřebujeme pokrýt od mobilu po retina desktop.
 */
const SRCSET_WIDTHS: Record<Variant, number[]> = {
	thumb: [120, 200, 320],
	display: [640, 960, 1280, 1600],
	full: [1280, 1920, 2400, 3200],
}

const CLOUDINARY_CLOUD_NAME = process.env.CLOUDINARY_CLOUD_NAME
/**
 * Zapnutí server-side image proxy. Pokud není, zachováme originální URL
 * (nechceme lámat fungující galerie, kde proxy ještě není nasazená).
 * Nastaví se přes env `IMAGE_PROXY_ENABLED=1` (webpack prohání env přes DefinePlugin).
 */
const IMAGE_PROXY_ENABLED = process.env.IMAGE_PROXY_ENABLED === '1'

const isAbsoluteHttp = (url: string) => /^https?:\/\//i.test(url)
const isDataUrl = (url: string) => url.startsWith('data:')
const isCloudinary = (url: string) =>
	/\/\/res\.cloudinary\.com\//i.test(url) ||
	(!!CLOUDINARY_CLOUD_NAME && url.includes(`/${CLOUDINARY_CLOUD_NAME}/image/upload/`))
const isLocalAdminUpload = (url: string) =>
	url.startsWith('/admin/uploads/') || url.includes('/admin/uploads/')

const canTransform = (url: string) => {
	if (!url || isDataUrl(url)) return false
	if (isCloudinary(url)) return true
	if (IMAGE_PROXY_ENABLED && (isLocalAdminUpload(url) || isAbsoluteHttp(url))) return true
	return false
}

/**
 * Aplikuje Cloudinary transformaci do URL, která má formát
 * `https://res.cloudinary.com/<cloud>/image/upload/<public>.jpg`.
 * Vkládá transformace těsně za segment `/upload/`.
 */
const applyCloudinaryTransform = (url: string, transform: string) => {
	const marker = '/upload/'
	const index = url.indexOf(marker)
	if (index === -1) return url
	const after = url.slice(index + marker.length)
	return `${url.slice(0, index + marker.length)}${transform}/${after}`
}

const buildCloudinaryUrl = (url: string, variant: Variant, widthOverride?: number) => {
	const opts = VARIANTS[variant]
	const width = widthOverride ?? opts.width
	// a_auto = auto-rotate dle EXIF, f_auto = nejlepší formát (avif/webp),
	// q_auto = adaptivní kvalita, c_fill pro thumb (ořezat) jinak c_limit.
	const crop = variant === 'thumb' ? 'c_fill,g_auto' : 'c_limit'
	const bits = ['a_auto', 'f_auto', `q_${opts.quality ?? 'auto'}`, crop, `w_${width}`, 'dpr_auto']
	if (opts.height && !widthOverride) bits.push(`h_${opts.height}`)
	return applyCloudinaryTransform(url, bits.join(','))
}

/**
 * Server proxy endpoint - zatím generovaný na klientu, backend implementace
 * je v `server/router/api/image-proxy-router.js`.
 */
const buildProxyUrl = (url: string, variant: Variant, widthOverride?: number) => {
	const opts = VARIANTS[variant]
	const width = widthOverride ?? opts.width
	const params = new URLSearchParams({ src: url, w: String(width) })
	if (opts.height && !widthOverride) params.set('h', String(opts.height))
	if (opts.quality) params.set('q', String(opts.quality))
	params.set('fit', variant === 'thumb' ? 'cover' : 'inside')
	return `/api/image-proxy?${params.toString()}`
}

export function resolveImageUrl(url: string, variant: Variant = 'display'): string {
	if (!url) return url
	if (isDataUrl(url)) return url

	if (isCloudinary(url)) return buildCloudinaryUrl(url, variant)
	if (IMAGE_PROXY_ENABLED && (isLocalAdminUpload(url) || isAbsoluteHttp(url))) {
		return buildProxyUrl(url, variant)
	}
	return url
}

/**
 * Vrátí `srcset` řetězec s více šířkami, nebo prázdný string pokud URL nejde
 * transformovat (externí nebo data URL) - v takovém případě se spoléháme
 * na jediný `src`.
 */
export function buildSrcSet(url: string, variant: Variant = 'display'): string {
	if (!canTransform(url)) return ''
	const widths = SRCSET_WIDTHS[variant]
	const parts: string[] = []
	for (const width of widths) {
		const built = isCloudinary(url)
			? buildCloudinaryUrl(url, variant, width)
			: buildProxyUrl(url, variant, width)
		parts.push(`${built} ${width}w`)
	}
	return parts.join(', ')
}

/**
 * `sizes` attribute pro hero (hlavní fotku). Vyjadřuje: obrázek zabírá
 * téměř celou šířku obrazovky na mobilu, ale na desktopu je limitován
 * kontejnerem cca 1024px.
 */
export const HERO_SIZES = '(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 1024px'

/**
 * `sizes` pro thumbnaily - mají fixní CSS šířku (cca 80px), takže stačí px hint.
 */
export const THUMB_SIZES = '80px'

/**
 * In-memory cache preloadů, ať nepouštíme stejný request vícekrát.
 * (Prohlížeč by sice mohl cache využít sám, ale HEAD/prefetch logika se liší
 * a my chceme mít jistotu, že nehoříme duplicitními requesty.)
 */
const preloadedUrls = new Set<string>()

/**
 * Pomocná utilita pro preload sousedních fotek.
 * Používá `new Image()` místo `<link rel=preload>`, aby to fungovalo i
 * po hydraci a bez zásahu do document.head.
 */
export function preloadImage(url: string, variant: Variant = 'display') {
	if (typeof window === 'undefined') return
	if (!url) return
	const resolved = resolveImageUrl(url, variant)
	if (preloadedUrls.has(resolved)) return
	preloadedUrls.add(resolved)
	const image = new Image()
	image.decoding = 'async'
	// Pro preload nepotřebujeme vysokou prioritu, nechceme brát zdroje
	// aktivně viditelné fotce.
	/** @type {any} */
	;(image as unknown as { fetchPriority: string }).fetchPriority = 'low'
	image.src = resolved
	const srcset = buildSrcSet(url, variant)
	if (srcset) {
		;(image as unknown as { srcset: string }).srcset = srcset
		;(image as unknown as { sizes: string }).sizes =
			variant === 'thumb' ? THUMB_SIZES : HERO_SIZES
	}
}

export function thumbUrl(url: string) {
	return resolveImageUrl(url, 'thumb')
}
export function displayUrl(url: string) {
	return resolveImageUrl(url, 'display')
}
export function fullUrl(url: string) {
	return resolveImageUrl(url, 'full')
}

export function thumbSrcSet(url: string) {
	return buildSrcSet(url, 'thumb')
}
export function displaySrcSet(url: string) {
	return buildSrcSet(url, 'display')
}
export function fullSrcSet(url: string) {
	return buildSrcSet(url, 'full')
}
